// 領収書発行ウィジェットが出す HTML（Issue #46）。
//
// ★★★ここに出てくる `wu-receipt-*` のクラス名は、各店のフリーページの <style> から参照される契約。
//     装飾（見出しの色・ボタンの画像・余白・リンクの見た目）を各店に持たせる設計なので、
//     **クラス名を変えると11ページの CSS が一斉に外れる**。
//     変えるときは全11ページの <style> も同時に直すこと。一覧と各店の設定値は同じフォルダの README.md にある。
//
// ★`panel` / `input-group` / `value` という裸の名前は使わない。
//   おちゃのこのテーマが読み込む Bootstrap と同じ名前で、2026-09-17 に11ページ全部が崩れた事故の原因そのもの。
//
// ★状態ごとに innerHTML を丸ごと差し替える（現行の ng-show のように全部置いて隠す形にはしない）。
//   隠す形だと、店の CSS が display を触った瞬間に「送信完了」と「発行画面」が同時に出るような壊れ方をする。
//   出ていない状態は DOM にも無い、が一番壊れにくい。

import { estDownloadUrl } from './api'
import { EST_TEXT, TEXT as T, type ReceiptKind } from './text'

/**
 * 画面の状態。現行の `c.mode`（1〜5）に対応する。
 * ★数字ではなく名前で持つ。1〜5 は AngularJS のテンプレートの都合であって、意味を持たない。
 */
export type State =
  /** 1: 発行画面 */
  | 'issue'
  /** 2: 送信完了 */
  | 'done'
  /** 3: 再送信画面（isIssued が true） */
  | 'reissue'
  /** 4: 処理中 */
  | 'sending'
  /** 5: エラー */
  | 'error'
  /** 現行に無い。guid が無い／受注が見つからない（現行は真っ白になる。text.ts の注記を参照） */
  | 'notFound'

/**
 * どちらの領収書か。通常版（メール送信）と別注版（PDF ダウンロード）で、文言と発行後に出すものが変わる。
 * ★通常版の呼び出しは引数を省略できる（既定 = 通常版）。別注版は est.ts が明示して渡す。
 */
export interface ViewContext {
  kind: ReceiptKind
  /** 別注版のダウンロードリンクに要る。通常版では使わない */
  guid: string | null
  /** 別注版のダウンロードリンクの基底 URL（data-api）。省略時は既定の口（api.ts の DEFAULT_API_BASE） */
  apiBase?: string
}

export const ORDER_VIEW: ViewContext = { kind: 'order', guid: null }

/** 表示に必要な値だけ。API の応答そのものではない（mailAddress など出す分だけを持つ）。 */
export interface ViewModel {
  siteName: string
  /** 通常版は数値、別注版は文字列 */
  orderNo: number | string
  name: string
  price: number
  date: string
  mailAddress: string
}

export interface IssueViewOptions {
  /** 但し書きの選択肢（data-descriptions） */
  descriptions: string[]
  /** いま選ばれている但し書き */
  description: string
}

export function esc(s: unknown): string {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  )
}

/** 金額の3桁区切り。現行の AngularJS `|number` フィルタと同じ見え方にする。 */
export function formatPrice(n: number): string {
  return Number.isFinite(n) ? Math.round(n).toLocaleString('en-US') : ''
}

function row(label: string, valueHtml: string): string {
  return (
    `<div class="wu-receipt-row">` +
    `<div class="wu-receipt-label">${esc(label)}</div>` +
    `<div class="wu-receipt-value">${valueHtml}</div>` +
    `</div>`
  )
}

/** 複数行の本文。空文字の要素は1行空ける（現行の <br /><br /> にあたる）。 */
function paragraph(lines: readonly string[]): string {
  const body = lines.map((l) => (l === '' ? '' : esc(l))).join('<br>')
  return `<p class="wu-receipt-lead">${body}</p>`
}

function title(text: string): string {
  return `<h2 class="wu-receipt-title">${esc(text)}</h2>`
}

/**
 * ボタン。ラベルは中の span に入れてある。
 * ★各店が画像ボタンに戻したいときは、この span を消して背景画像を敷く:
 *     .wu-receipt-button{ background:url(...) no-repeat center/contain; width:280px; height:56px; padding:0; border:none; }
 *     .wu-receipt-button-label{ display:none; }
 *   span が無いと、画像にしたとき読み上げと「画像が出ないときの代わりの文字」を同時に失う。
 */
function button(label: string, kind: string): string {
  return (
    `<div class="wu-receipt-actions">` +
    `<button type="button" class="wu-receipt-button" data-wu-receipt-action="${esc(kind)}">` +
    `<span class="wu-receipt-button-label">${esc(label)}</span>` +
    `</button></div>`
  )
}

/**
 * 検証エラーの中身（`<li>` の並び）。
 * ★入れ物の `<ul>` は常に DOM に置いたままで、中身と表示/非表示だけを差し替える。
 *   宛名を打つたびに画面ごと組み直すと、入力欄からフォーカスが外れて日本語入力が切れる。
 */
export function errorItemsHtml(errors: readonly string[]): string {
  return errors.map((e) => `<li class="wu-receipt-error-item">${esc(e)}</li>`).join('')
}

/**
 * 別注版の「領収書ダウンロード」。ボタンではなくリンク（PDF を開くだけで、押しても API は叩かない）。
 * ★画像にしたい店は wu-receipt-button と同じ要領で `.wu-receipt-download` に背景画像を敷き、
 *   `.wu-receipt-download-label` を display:none にする。
 * ★guid が無い状態（プレビュー以外では起きない）ではリンク先を持たないので、href を付けない。
 */
function downloadLink(label: string, ctx: ViewContext): string {
  const href = ctx.guid === null ? '' : ` href="${esc(estDownloadUrl(ctx.guid, ctx.apiBase))}"`
  return (
    `<div class="wu-receipt-actions">` +
    `<a class="wu-receipt-download"${href} data-wu-receipt-download="est">` +
    `<span class="wu-receipt-download-label">${esc(label)}</span>` +
    `</a></div>`
  )
}

/** mode 1: 発行画面 */
export function issueView(vm: ViewModel, o: IssueViewOptions, ctx: ViewContext = ORDER_VIEW): string {
  const text = ctx.kind === 'est' ? EST_TEXT.issue : T.issue
  const options = o.descriptions
    .map((d) => `<option value="${esc(d)}"${d === o.description ? ' selected' : ''}>${esc(d)}</option>`)
    .join('')

  const rows =
    row(T.label.siteName, esc(vm.siteName)) +
    row(T.label.orderNo, esc(vm.orderNo)) +
    row(
      T.label.name,
      `<input type="text" class="wu-receipt-input" data-wu-receipt-field="name" value="${esc(vm.name)}" maxlength="200">` +
        `<span class="wu-receipt-unit">${esc(T.unit.honorific)}</span>`,
    ) +
    row(T.label.description, `<select class="wu-receipt-select" data-wu-receipt-field="description">${options}</select>`) +
    row(
      T.label.price,
      `<span class="wu-receipt-price">${esc(formatPrice(vm.price))}</span>` +
        `<span class="wu-receipt-yen">${esc(T.unit.yen)}</span>` +
        `<span class="wu-receipt-help">${esc(T.unit.taxIncluded)}</span>`,
    ) +
    row(
      T.label.date,
      `<span class="wu-receipt-date">${esc(vm.date)}</span>` +
        `<span class="wu-receipt-help">${esc(T.unit.dateHelp)}</span>`,
    ) +
    // 別注版は「ご登録メールアドレス」の行を出さない（4 ページともコメントアウトで非表示だった）
    (ctx.kind === 'est' ? '' : row(T.label.mailAddress, `<span class="wu-receipt-mail">${esc(vm.mailAddress)}</span>`))

  return (
    `<div class="wu-receipt-state wu-receipt-state-issue">` +
    title(text.title) +
    paragraph(text.lead) +
    // 中身は widget.ts が入れる（入力のたびにここだけ差し替える）。style は CSS より強いので店に消されない。
    `<ul class="wu-receipt-errors" data-wu-receipt-field="errors" style="display:none"></ul>` +
    `<div class="wu-receipt-box">${rows}</div>` +
    button(text.button, 'issue') +
    `</div>`
  )
}

/** mode 2: 送信完了（通常版）／領収書 ダウンロード（別注版） */
export function doneView(topUrl: string, ctx: ViewContext = ORDER_VIEW): string {
  if (ctx.kind === 'est') {
    return (
      `<div class="wu-receipt-state wu-receipt-state-done">` +
      title(EST_TEXT.done.title) +
      paragraph(EST_TEXT.done.body) +
      downloadLink(EST_TEXT.done.download, ctx) +
      `<p class="wu-receipt-note"><a class="wu-receipt-toplink" href="${esc(topUrl)}">${esc(EST_TEXT.done.topLink)}</a></p>` +
      `</div>`
    )
  }
  return (
    `<div class="wu-receipt-state wu-receipt-state-done">` +
    title(T.done.title) +
    paragraph(T.done.body) +
    `<p class="wu-receipt-note"><a class="wu-receipt-toplink" href="${esc(topUrl)}">${esc(T.done.topLink)}</a></p>` +
    `</div>`
  )
}

/**
 * mode 3: 再送信画面（通常版）／領収書再ダウンロードページ（別注版）。
 * ★別注版にはボタンが無い。発行済みなので API は叩かず、ダウンロードのリンクだけを出す（現行どおり）。
 */
export function reissueView(ctx: ViewContext = ORDER_VIEW): string {
  if (ctx.kind === 'est') {
    return (
      `<div class="wu-receipt-state wu-receipt-state-reissue">` +
      title(EST_TEXT.reissue.title) +
      paragraph(EST_TEXT.reissue.lead) +
      downloadLink(EST_TEXT.reissue.download, ctx) +
      `</div>`
    )
  }
  return (
    `<div class="wu-receipt-state wu-receipt-state-reissue">` +
    title(T.reissue.title) +
    paragraph(T.reissue.lead) +
    button(T.reissue.button, 'reissue') +
    `</div>`
  )
}

/** mode 4: 処理中 */
export function sendingView(): string {
  return (
    `<div class="wu-receipt-state wu-receipt-state-sending">` +
    `<p class="wu-receipt-lead">${esc(T.sending.body)}</p>` +
    `</div>`
  )
}

/** mode 5: エラー */
export function errorView(): string {
  return `<div class="wu-receipt-state wu-receipt-state-error">${paragraph(T.error.body)}</div>`
}

/** 現行に無い状態。guid が無い／受注が見つからない */
export function notFoundView(): string {
  return `<div class="wu-receipt-state wu-receipt-state-notfound">${paragraph(T.notFound.body)}</div>`
}
